import { Pill, CheckCircle, XCircle, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { Medication, MedicationStatus } from "@/types/vision360";

interface Vision360MedicationsProps {
  medications: Medication[];
}

const getStatusConfig = (status: MedicationStatus) => {
  switch (status) {
    case "em_uso":
      return {
        icon: <CheckCircle className="h-3.5 w-3.5 text-green-500" />,
        label: "Em uso",
        badgeClass: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
      };
    case "suspenso":
      return {
        icon: <XCircle className="h-3.5 w-3.5 text-red-500" />,
        label: "Suspenso",
        badgeClass: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
      };
    case "pontual":
      return {
        icon: <Clock className="h-3.5 w-3.5 text-amber-500" />,
        label: "Pontual",
        badgeClass: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
      };
    default:
      return {
        icon: <Pill className="h-3.5 w-3.5 text-gray-500" />,
        label: "Desconhecido",
        badgeClass: "bg-gray-100 text-gray-700",
      };
  }
};

export function Vision360Medications({ medications }: Vision360MedicationsProps) {
  if (medications.length === 0) {
    return (
      <div className="text-center py-4 text-muted-foreground text-sm">
        Nenhum medicamento registrado
      </div>
    );
  }

  // Agrupar por status
  const grouped = {
    em_uso: medications.filter((m) => m.status === "em_uso"),
    pontual: medications.filter((m) => m.status === "pontual"),
    suspenso: medications.filter((m) => m.status === "suspenso"),
  };

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
        <Pill className="h-4 w-4" />
        Medicamentos ({medications.length})
      </h4>

      {grouped.em_uso.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-green-600 dark:text-green-400">Em Uso</p>
          {grouped.em_uso.map((med, i) => (
            <MedicationItem key={i} medication={med} />
          ))}
        </div>
      )}

      {grouped.pontual.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-amber-600 dark:text-amber-400">Uso Pontual</p>
          {grouped.pontual.map((med, i) => (
            <MedicationItem key={i} medication={med} />
          ))}
        </div>
      )}

      {grouped.suspenso.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-red-600 dark:text-red-400">Suspensos</p>
          {grouped.suspenso.map((med, i) => (
            <MedicationItem key={i} medication={med} />
          ))}
        </div>
      )}
    </div>
  );
}

function MedicationItem({ medication }: { medication: Medication }) {
  const config = getStatusConfig(medication.status);

  return (
    <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
      {config.icon}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{medication.name}</p>
        {(medication.dose || medication.frequency) && (
          <p className="text-xs text-muted-foreground">
            {[medication.dose, medication.frequency].filter(Boolean).join(" • ")}
          </p>
        )}
      </div>
      <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", config.badgeClass)}>
        {config.label}
      </Badge>
    </div>
  );
}
